import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import TimesheetProjectGroup from "./TimesheetProjectGroup";
import TaskDetailDrawer from "@/components/tools/Tasks/TaskDetailDrawer";
import { parseDateKey } from "./WeekNavigator";
import {
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
} from "@/lib/backend";
import type { TimeEntry, Timesheet } from "@/lib/backend";
import type { ProjectSummary, Task, TaskType } from "@/lib/types";
import { toast } from "@/hooks/use-toast";

const TASK_TYPE_ORDER: TaskType[] = ["Design", "Coordination", "Submittal", "RFI/ASI", "PCR/PCO", "Deliverable"];
const ACTIVE_STATUSES = new Set(["pending", "in-progress", "in_progress", "coordinating"]);

function groupByType(taskList: Task[]): [TaskType | "Other", Task[]][] {
  const groups = new Map<string, Task[]>();
  for (const t of taskList) {
    const key = t.task_type ?? "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(t);
  }
  const ordered: [TaskType | "Other", Task[]][] = [];
  for (const type of TASK_TYPE_ORDER) {
    if (groups.has(type)) ordered.push([type, groups.get(type)!]);
  }
  if (groups.has("Other")) ordered.push(["Other", groups.get("Other")!]);
  return ordered;
}

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface TimesheetGridProps {
  timesheet: Timesheet;
  entries: TimeEntry[];
  projects: ProjectSummary[];
  allTasks: Task[];
  periodStart: string;
  weekStartDay: number;
  onEntriesChange: (entries: TimeEntry[]) => void;
  isDemo?: boolean;
}

function buildWeekDates(periodStart: string): string[] {
  const dates: string[] = [];
  const start = parseDateKey(periodStart);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

interface AddEntryState {
  projectId: string;
  taskSearch: string;
  selectedTask: Task | null;
  description: string;
}

// ---------------------------------------------------------------------------
// Unified "Add Entry" modal — task picker + description input in one view
// ---------------------------------------------------------------------------

interface AddEntryModalProps {
  open: boolean;
  projectName?: string;
  allTasks: Task[];
  projectId: string;
  taskSearch: string;
  selectedTask: Task | null;
  description: string;
  onTaskSearchChange: (v: string) => void;
  onTaskSelect: (task: Task) => void;
  onDescriptionChange: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

function AddEntryModal({
  open,
  projectName,
  allTasks,
  projectId,
  taskSearch,
  selectedTask,
  description,
  onTaskSearchChange,
  onTaskSelect,
  onDescriptionChange,
  onSubmit,
  onClose,
}: AddEntryModalProps) {
  const projectTasks = useMemo(
    () => allTasks.filter((t) => t.project_id === projectId),
    [allTasks, projectId],
  );

  const filtered = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    if (!q) return projectTasks;
    return projectTasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.task_type ?? "").toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q),
    );
  }, [projectTasks, taskSearch]);

  const activeTasks = filtered.filter((t) => ACTIVE_STATUSES.has(t.status));
  const completedTasks = filtered.filter((t) => !ACTIVE_STATUSES.has(t.status));
  const activeGroups = groupByType(activeTasks);
  const completedGroups = groupByType(completedTasks);

  const canSubmit = !!selectedTask || description.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <DialogTitle className="text-sm font-semibold">
            Add Entry{projectName ? ` — ${projectName}` : ""}
          </DialogTitle>
          <p className="text-xs text-white/40 mt-0.5">
            Link a task <span className="text-white/25">or</span> enter a description — one is required.
          </p>
        </DialogHeader>

        {/* Task search section */}
        <div className="shrink-0 px-4 pb-1">
          <p className="text-[11px] font-medium text-white/50 uppercase tracking-wide mb-1">Link a task</p>
        </div>
        <Command className="border-none rounded-none border-t border-white/10 shrink-0">
          <CommandInput
            placeholder="Search tasks…"
            value={taskSearch}
            onValueChange={onTaskSearchChange}
            className="text-sm"
          />
          <CommandList className="max-h-48 overflow-y-auto">
            {activeTasks.length === 0 && completedTasks.length === 0 && (
              <CommandEmpty className="text-xs py-4">
                {taskSearch ? "No tasks match your search." : "No tasks for this project."}
              </CommandEmpty>
            )}
            {activeGroups.map(([type, groupTasks]) => (
              <CommandGroup key={type} heading={type} className="text-xs">
                {groupTasks.map((task) => (
                  <CommandItem
                    key={task.id}
                    value={task.id}
                    onSelect={() => onTaskSelect(task)}
                    className={`text-xs cursor-pointer ${selectedTask?.id === task.id ? "bg-primary/20 text-primary" : ""}`}
                  >
                    <span className="truncate">{task.title}</span>
                    {selectedTask?.id === task.id && (
                      <span className="ml-auto text-[10px] text-primary shrink-0">Selected</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {completedGroups.length > 0 && activeGroups.length > 0 && <CommandSeparator />}
            {completedGroups.length > 0 && (
              <CommandGroup heading="Completed" className="text-xs opacity-60">
                {completedGroups.flatMap(([, groupTasks]) =>
                  groupTasks.map((task) => (
                    <CommandItem
                      key={task.id}
                      value={task.id}
                      onSelect={() => onTaskSelect(task)}
                      className="text-xs cursor-pointer"
                    >
                      <span className="truncate line-through opacity-70">{task.title}</span>
                      <span className="ml-auto text-[10px] text-white/40">{task.task_type}</span>
                    </CommandItem>
                  )),
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>

        {/* Divider */}
        <div className="flex items-center gap-3 px-4 py-2 shrink-0">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[11px] text-white/30 font-medium">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Description section */}
        <div className="px-4 pb-4 shrink-0">
          <p className="text-[11px] font-medium text-white/50 uppercase tracking-wide mb-2">Enter a description</p>
          <Input
            placeholder="e.g. Site coordination meeting"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) onSubmit(); }}
            className="text-sm"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 pb-4 pt-1 border-t border-white/10 shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!canSubmit} onClick={onSubmit}>Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TimesheetGrid({
  timesheet,
  entries,
  projects,
  allTasks,
  periodStart,
  onEntriesChange,
  isDemo = false,
}: TimesheetGridProps) {
  const weekDates = buildWeekDates(periodStart);
  const readOnly = timesheet.status !== "draft";

  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");

  const [addEntry, setAddEntry] = useState<AddEntryState | null>(null);

  const [detailTask, setDetailTask] = useState<Task | null>(null);

  // Projects that have at least one entry this week
  const activeProjectIds = Array.from(new Set(entries.map((e) => e.project_id)));
  const projectMap = new Map(projects.map((p) => [p.id, p]));

  // Projects with entries, preserving order of first appearance
  const activeProjects = activeProjectIds
    .map((id) => projectMap.get(id))
    .filter((p): p is ProjectSummary => !!p);

  const handleAddProject = (projectId: string) => {
    setAddProjectOpen(false);
    setProjectSearch("");
    setAddEntry({ projectId, taskSearch: "", selectedTask: null, description: "" });
  };

  const handleAddEntry = (projectId: string) => {
    setAddEntry({ projectId, taskSearch: "", selectedTask: null, description: "" });
  };

  const handleAddEntrySubmit = async () => {
    if (!addEntry) return;
    const { projectId, selectedTask, description } = addEntry;
    if (isDemo) {
      const now = new Date().toISOString();
      const demoEntry: TimeEntry = {
        id: `demo-entry-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        timesheet_id: timesheet.id,
        user_id: "demo",
        project_id: projectId,
        task_id: selectedTask?.id ?? null,
        entry_date: weekDates[0],
        hours: 0,
        description: selectedTask ? null : description.trim() || null,
        created_at: now,
        updated_at: now,
      };
      onEntriesChange([...entries, demoEntry]);
      setAddEntry(null);
      return;
    }
    try {
      const entry = await createTimeEntry(timesheet.id, {
        project_id: projectId,
        task_id: selectedTask?.id ?? null,
        entry_date: weekDates[0],
        hours: 0,
        description: selectedTask ? null : description.trim(),
      });
      onEntriesChange([...entries, entry]);
    } catch (e: any) {
      toast({ title: "Failed to add entry", description: e?.message ?? "Unknown error", variant: "destructive" });
    }
    setAddEntry(null);
  };

  const handleSaveHours = async (
    _lineKey: string,
    projectId: string,
    taskId: string | null,
    description: string | null,
    entryDate: string,
    hours: number,
    existingEntryId: string | null
  ) => {
    if (isDemo) {
      const now = new Date().toISOString();
      if (existingEntryId) {
        onEntriesChange(entries.map((e) =>
          e.id === existingEntryId ? { ...e, hours, updated_at: now } : e,
        ));
      } else {
        const demoEntry: TimeEntry = {
          id: `demo-entry-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timesheet_id: timesheet.id,
          user_id: "demo",
          project_id: projectId,
          task_id: taskId,
          entry_date: entryDate,
          hours,
          description: description ?? null,
          created_at: now,
          updated_at: now,
        };
        onEntriesChange([...entries, demoEntry]);
      }
      return;
    }
    try {
      if (existingEntryId) {
        const updated = await updateTimeEntry(timesheet.id, existingEntryId, { hours });
        onEntriesChange(entries.map((e) => (e.id === existingEntryId ? updated : e)));
      } else {
        const entry = await createTimeEntry(timesheet.id, {
          project_id: projectId,
          task_id: taskId,
          entry_date: entryDate,
          hours,
          description: description ?? undefined,
        });
        onEntriesChange([...entries, entry]);
      }
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (isDemo) {
      onEntriesChange(entries.filter((e) => e.id !== entryId));
      return;
    }
    try {
      await deleteTimeEntry(timesheet.id, entryId);
      onEntriesChange(entries.filter((e) => e.id !== entryId));
    } catch (e: any) {
      toast({ title: "Delete failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    }
  };

  const projectById = new Map(projects.map((p) => [p.id, p]));

  return (
    <div className="min-w-max">
      <table className="w-full text-xs border-collapse">
        {/* Column headers */}
        <thead className="sticky top-0 z-10 bg-[#0e0e14] text-purple-100 border-b border-white/10">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-white/80 w-64">Activity</th>
            {weekDates.map((dateKey) => {
              const d = parseDateKey(dateKey);
              const dayName = DAY_SHORT[d.getUTCDay()];
              const dayNum = d.getUTCDate();
              return (
                <th key={dateKey} className="px-1 py-2 text-center font-medium text-white/60 w-16">
                  <div className="text-[10px] text-white/40">{dayName}</div>
                  <div className="text-xs">{dayNum}</div>
                </th>
              );
            })}
            <th className="px-2 py-2 text-center font-semibold text-white/70 w-16">Total</th>
            <th className="w-8" />
          </tr>
        </thead>

        <tbody>
          {/* One group per project */}
          {activeProjects.map((project) => {
            const projectEntries = entries.filter((e) => e.project_id === project.id);
            return (
              <TimesheetProjectGroup
                key={project.id}
                projectId={project.id}
                projectName={project.project_name}
                clientName={project.client_name}
                city={project.city}
                state={project.state}
                entries={projectEntries}
                tasks={allTasks}
                weekDates={weekDates}
                timesheetStatus={timesheet.status}
                onSaveHours={handleSaveHours}
                onDeleteEntry={handleDeleteEntry}
                onAddEntry={handleAddEntry}
                onTaskClick={setDetailTask}
              />
            );
          })}

          {/* Add project row */}
          {!readOnly && (
            <tr>
              <td colSpan={weekDates.length + 3} className="px-3 py-3">
                <button
                  className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition-colors"
                  onClick={() => setAddProjectOpen(true)}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add project
                </button>
              </td>
            </tr>
          )}

          {activeProjects.length === 0 && readOnly && (
            <tr>
              <td colSpan={weekDates.length + 3} className="px-3 py-8 text-center text-xs text-white/30">
                No time entries for this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Add Project Combobox Dialog */}
      <Dialog open={addProjectOpen} onOpenChange={setAddProjectOpen}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm font-semibold">Add Project</DialogTitle>
          </DialogHeader>
          <Command className="border-none rounded-none">
            <CommandInput
              placeholder="Search projects…"
              value={projectSearch}
              onValueChange={setProjectSearch}
              className="text-sm"
            />
            <CommandList className="max-h-64">
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandGroup>
                {projects
                  .filter((p) => {
                    const q = projectSearch.trim().toLowerCase();
                    return !q || p.project_name.toLowerCase().includes(q) || (p.client_name ?? "").toLowerCase().includes(q);
                  })
                  .map((project) => (
                    <CommandItem
                      key={project.id}
                      value={project.id}
                      onSelect={() => handleAddProject(project.id)}
                      className="text-xs cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span>{project.project_name}</span>
                        {project.client_name && (
                          <span className="text-white/40 text-[10px]">{project.client_name}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Unified Add Entry Modal */}
      {addEntry && (
        <AddEntryModal
          open
          projectName={projectMap.get(addEntry.projectId)?.project_name}
          allTasks={allTasks}
          projectId={addEntry.projectId}
          taskSearch={addEntry.taskSearch}
          selectedTask={addEntry.selectedTask}
          description={addEntry.description}
          onTaskSearchChange={(v) => setAddEntry((p) => p ? { ...p, taskSearch: v, selectedTask: null } : null)}
          onTaskSelect={(task) => setAddEntry((p) => p ? { ...p, selectedTask: task, description: "" } : null)}
          onDescriptionChange={(v) => setAddEntry((p) => p ? { ...p, description: v, selectedTask: null } : null)}
          onSubmit={handleAddEntrySubmit}
          onClose={() => setAddEntry(null)}
        />
      )}

      {/* Task Detail Drawer */}
      <TaskDetailDrawer
        task={detailTask}
        projectById={projectById as any}
        onClose={() => setDetailTask(null)}
      />
    </div>
  );
}
